import { StyleProvider } from '@ant-design/cssinjs';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { ConfigProvider, App as AntdApp, theme as antdTheme } from 'antd';
import enGB from 'antd/locale/en_GB';
import type { ReactNode } from 'react';

import { resolveAccentColours, useAccent } from '@/shell/accent';
import { textScales, useTextScale } from '@/shell/textScale';
import { useThemeMode } from '@/shell/themeMode';
import { theme } from '@/theme';

/**
 * Everything Ant Design needs in order to agree with the stylesheet.
 *
 * A component rather than inline JSX in `main.tsx`, because it has to *subscribe* to the theme
 * mode. The template switches its own palette in CSS and needs nobody's help; Ant Design resolves
 * its tokens in JavaScript at render time, so without a re-render on the toggle the page goes dark
 * around a table that stays white. That was a real defect, not a hypothetical.
 */
export function AppProviders({
  queryClient,
  children,
}: {
  queryClient: QueryClient;
  children: ReactNode;
}) {
  const mode = useThemeMode();
  /*
    Subscribed for the reason the mode above is: Ant Design resolves its tokens in JavaScript at
    render time, so without a re-render the template would change size around a table that stayed
    at the old one. The CSS half of the same setting is written to the root element by
    `applyTextScale`, which `App` calls — a custom property is not something re-rendering reaches.
  */
  const scale = useTextScale();
  const { fontSize, controlHeight } = textScales[scale];

  /*
    Subscribed for the same reason, and it is not decorative. Ant Design's `colorLink`, `colorInfo`
    and every selected-row surface come from `theme.ts` at render time; the template's half is a CSS
    attribute. Without this the sidebar and the cards would change colour around a table, a Select
    and a date picker that stayed on the old accent.

    Read from the DOM rather than passed as a token: the trios live in `style.css` under
    `[data-accent]`, so this reads back what is in force instead of holding a second copy of three
    colours that would go stale the day one is retuned.
  */
  useAccent();
  const brand = resolveAccentColours(mode);

  return (
    /*
      `layer` is the load-bearing prop here, not a nicety.

      Ant Design injects its CSS into <head> at runtime, unlayered. The template's CSS is entirely
      inside `@layer components`. In the cascade an unlayered declaration beats a layered one
      **regardless of specificity** — so antd's `.ant-app a { color: <colorLink> }` was overriding
      `.sidebar .sidebar-menu > ul > li > a.subdrop`, and every sidebar link rendered brand teal
      instead of the template's dark-on-slate. Nothing about that is visible in either stylesheet;
      each one is correct on its own.

      `layer` wraps antd's output in `@layer antd`. `index.css` then places that layer between
      Tailwind's Preflight and the template's own rules — read the comment there before changing
      either end of it, because both neighbours were arrived at by breaking them. This is the fix
      for the whole class of "the theme looks right until an antd component is on the page", not
      just for the sidebar it was found in.
    */
    <StyleProvider layer>
      {/*
        en_GB rather than en_US: day-month-year date entry and DD/MM/YYYY display are what Indian
        users expect, and getting this wrong turns 01/02 into the wrong month on every screen.
        Gujarati and Hindi are added here in Phase 6 alongside the resource bundles (spec 24.2).
      */}
      <ConfigProvider
        theme={{
          ...theme,
          algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
          token: {
            ...theme.token,
            // The two the scale owns. They live here rather than in `theme.ts` because that file is
            // a module constant and this one is a setting — and because the dark branch below used
            // to replace `token` wholesale, so a size set there would have been dropped in dark
            // mode only. One object now, which is what makes that impossible.
            fontSize,
            controlHeight,

            // The accent's three. `colorPrimary` is deliberately absent: slate stays the action
            // colour, which is the rule the whole design runs on — the brand marks identity and
            // state, never "click me".
            colorLink: brand.brand,
            colorInfo: brand.brand,
            controlItemBgActive: brand.wash,
            controlItemBgActiveHover: brand.washHover,
            // The algorithm derives contrast; these name the surfaces the template already named,
            // so the two palettes cannot drift apart. Spread last, so the values that carry meaning
            // rather than contrast — the brand teal, the amber — still win.
            ...(mode === 'dark' ? darkTokens : {}),
          },
        }}
        locale={enGB}
      >
        {/*
          Supplies the message/notification/modal context that hooks use, so nothing has to reach
          for AntD's static methods — those bypass ConfigProvider and silently lose the theme.

          Toasts are `notification`, not `message`, and the placement below is the whole reason:
          `message` is always horizontally centred and has no placement option, so a top-centre
          banner sat directly over the page heading and the primary action beside it. The top-right
          corner is out of the reading path and is where this shell already puts things the system
          says — it is the corner the notification bell lives in.

          Errors linger about twice as long as confirmations. A confirmation repeats something the
          user can already see on the page behind it; a failure is the only account of something
          that did not happen, and four seconds is not long to read one and decide what to do.
        */}
        <AntdApp notification={{ placement: 'topRight', duration: 4 }}>
          <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        </AntdApp>
      </ConfigProvider>
    </StyleProvider>
  );
}

/**
 * Copied from the template's dark `@theme` block, with the variable each one mirrors named.
 *
 * Worth knowing before editing: the template inverts what its colour *names* resolve to rather
 * than what they mean, so in dark mode `--color-white` is `#1A1A1A`. Reading these as literal
 * colours is how they end up "corrected" back to something wrong.
 */
const darkTokens = {
  /** `--color-white` — the card surface. Cards, tables and the sidebar all sit on it. */
  colorBgContainer: '#1A1A1A',
  colorBgElevated: '#1A1A1A',
  /** `--color-border-color` — every hairline in the product. */
  colorBorder: '#27292E',
  colorBorderSecondary: '#27292E',
  /** `--color-light` — the page behind the cards. */
  colorBgLayout: '#1A1E22',
  /** `--color-gray-900` and `--color-default`: text roles here, not greys. */
  colorText: '#A7B2C2',
  colorTextBase: '#A7B2C2',
  colorTextSecondary: '#818993',
  colorTextTertiary: '#818993',
};
